<template>
  <div>
    <basic-portlet title="Advanced Podcast Settings" :no-shadow="borderless">
      <div class="row">
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label for="publish_date"><strong>Publish Date * <span id="publish_date_tooltip" class="ml-2 la la-question-circle" /></strong></label>
            <b-tooltip target="publish_date_tooltip" triggers="hover">
              <div v-sanitise="tooltips.publish_date_tooltip" />
            </b-tooltip>

            <datetime id="itunes_detail_publish_date"
                      v-model="podcast.itunes_detail.publish_date"
                      v-validate="'required'"
                      data-vv-scope="advanced-form"
                      name="Publish Date"
                      input-class="form-control"
                      type="date"
                      format="yyyy-MM-dd" />
            <p v-show="errors.has('Publish Date', 'advanced-form')" class="help text-ss-danger">
              {{ errors.first('Publish Date', 'advanced-form') }}
            </p>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="form-group">
            <label for="new_feed_url"><strong>New Feed URL <span id="new_feed_url_tooltip" class="ml-2 la la-question-circle" /></strong></label>
            <b-tooltip target="new_feed_url_tooltip" triggers="hover">
              <div v-sanitise="tooltips.new_feed_url_tooltip" />
            </b-tooltip>
            <input id="new_feed_url"
                   v-model="podcast.itunes_detail.new_feed_url"
                   v-validate="{url: {require_protocol: true }}"
                   data-vv-scope="advanced-form"
                   name="New Feed Url"
                   type="text"
                   class="form-control"
                   placeholder="https://a-new-feed-url.com/my-feed.xml">
            <p v-show="errors.has('New Feed Url', 'advanced-form')" class="help text-ss-danger">
              {{ errors.first('New Feed Url', 'advanced-form') }}
            </p>
          </div>
        </div>
      </div>
    </basic-portlet>

    <basic-portlet title="Advanced Timbre Settings" :no-shadow="borderless">
      <div class="row">
        <div class="col-12 col-sm-6">
          <div class="form-group">
            <label for="storage_url_prefix"><strong>Media URL Prefix <span id="storage_url_prefix_tooltip" class="ml-2 la la-question-circle" /></strong></label>
            <b-tooltip target="storage_url_prefix_tooltip" triggers="hover">
              <div v-sanitise="tooltips.storage_url_prefix_tooltip" />
            </b-tooltip>
            <input id="new_feed_url"
                   v-model="podcast.storage_url_prefix"
                   v-validate="{url: {require_protocol: true }}"
                   data-vv-scope="advanced-form"
                   name="Media URL Prefix"
                   type="text"
                   class="form-control"
                   placeholder="https://p.podderapp.com/xxxxxxxxxx">
            <p v-show="errors.has('Media URL Prefix', 'advanced-form')" class="help text-ss-danger">
              {{ errors.first('Media URL Prefix', 'advanced-form') }}
            </p>
          </div>
        </div>
        <div class="col-12 col-sm-6">
          <div class="form-group">
            <label><strong>{{ getItemLabel(true) }} Ordering<span id="ordering_tooltip" class="ml-2 la la-question-circle" /></strong></label>
            <b-tooltip target="ordering_tooltip" triggers="hover">
              <div v-sanitise="tooltips.ordering_tooltip" />
            </b-tooltip>
            <radio-group v-model="podcast.ordering"
                         group-name="feed_ordering"
                         :options="ordering_options" />
          </div>
        </div>
        <div class="col-12 col-sm-6">
          <div class="form-group">
            <label for="keywords"><strong>Tags <span id="tags_tooltip" class="ml-2 la la-question-circle" /></strong></label>
            <b-tooltip target="tags_tooltip" triggers="hover">
              <div v-sanitise="tooltips.tags_tooltip" />
            </b-tooltip>
            <active-passive :data-val="ms_tag_values" bordered>
              <template #ap-passive>
                <multi-select-tag-bag :tags-list="ms_tag_values" :limit="20" :show-controls="false" />
              </template>
              <template #ap-active>
                <multiselect v-model="ms_tag_values"
                             :options="auto_complete_tags"
                             :multiple="true"
                             :taggable="true"
                             placeholder="Search or add tags..."
                             @tag="addTag" />
              </template>
            </active-passive>
            <!-- <multiselect v-model="ms_tag_values"
                         :options="auto_complete_tags"
                         :multiple="true"
                         :taggable="true"
                         placeholder="Search or add tags..."
                         @tag="addTag" /> -->
          </div>
        </div>
        <div class="col-12 col-sm-6">
          <label><strong>Syncing</strong></label>
          <div class="form-group">
            <ss-checkbox id="feed-auto-sync-items-tags" v-model="podcast.auto_sync_items_by_tags">
              <span class="ml-2">Automatically sync {{ getItemLabel(true) }} with matching tags</span><span id="sync_items_tooltip" class="ml-2 la la-question-circle" />
            </ss-checkbox>
            <b-tooltip target="sync_items_tooltip" triggers="hover">
              <div v-sanitise="tooltips.sync_items_tooltip" />
            </b-tooltip>
          </div>
          <div class="form-group">
            <ss-checkbox id="feed-sync-to-website" v-model="podcast.sync_to_website">
              <span class="ml-2">Sync to Website</span><span id="website_sync_tooltip" class="ml-2 la la-question-circle" />
            </ss-checkbox>
            <b-tooltip target="website_sync_tooltip" triggers="hover">
              <div v-sanitise="tooltips.website_sync_tooltip" />
            </b-tooltip>
          </div>
        </div>
      </div>
    </basic-portlet>
  </div>
</template>

<script>
import RadioGroup from "VueComponents/RadioGroup";
import { mapActions, mapWritableState } from 'pinia';
import { usePodcastManageStore } from "VueStore/app/podcastManage";
import PodcastValidateMixin from "VueMixins/PodcastValidateMixin";
import TooltipsMixin from "VueMixins/TooltipsMixin";
import ActivePassive from 'VueComponents/ActivePassive';
import MultiSelectTagBag from "VueComponents/MultiSelectTagBag";

export default {
  components: {
    RadioGroup,
    ActivePassive,
    MultiSelectTagBag,
  },
  mixins: [PodcastValidateMixin, TooltipsMixin],
  props: {
    borderless: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapWritableState(usePodcastManageStore, ['podcast']),
    ...mapWritableState(usePodcastManageStore, ['ordering_options']),
    ...mapWritableState(usePodcastManageStore, ['ms_tag_values']),
    ...mapWritableState(usePodcastManageStore, ['auto_complete_tags']),
  },
  mounted() {
    this.getAllTags();
  },
  methods: {
    ...mapActions(usePodcastManageStore, ['getAllTags']),
    ...mapActions(usePodcastManageStore, ['addTag']),
  },
};
</script>
